import Gallery, { GalleryFillRow } from "components/Media/Gallery";
import { createDate, importAll, transformImages } from "utils";

import AdvancedPotionMakingEditionII from "../../edition-II";
import AdvancedPotionMakingPrincesCopy from "../../edition-II-prince's-copy";
import { Link } from "react-router-dom";
import ReplicaContent from "components/Replicas/ReplicaContent";
import ReplicaParagraph from "components/Replicas/ReplicaParagraph";
import ReplicaQuote from "components/Replicas/ReplicaQuote";
import ReplicaSpacer from "components/Replicas/ReplicaSpacer";
import ReplicaSubTitleAnchor from "components/Replicas/ReplicaSubTitleAnchor";
import { ReplicaVersionType } from "types";
import YouTubeVideo from "components/Media/YouTubeVideo";
import background from "./background.jpg";
import { routes } from "config";
import thumbnail from "./thumbnail.png";

let gallery = [];
let gallery_EditionIIvsIV_1 = [];
let gallery_EditionIIvsIV_2 = [];

try {
  gallery = importAll(
    (require as any).context("./gallery", false, /\.(png|jpe?g|svg)$/),
  );
  gallery_EditionIIvsIV_1 = importAll(
    (require as any).context(
      "./Gallery - Edition II vs IV - 1",
      false,
      /\.(png|jpe?g|svg)$/,
    ),
  );
  gallery_EditionIIvsIV_2 = importAll(
    (require as any).context(
      "./Gallery - Edition II vs IV - 2",
      false,
      /\.(png|jpe?g|svg)$/,
    ),
  );
} catch (e) {
  console.warn(e);
}

/* VERSION DATA */
export const VERSION_DATA: ReplicaVersionType = {
  isWIP: false,
  page: undefined,
  dateCreated: createDate({ day: 20, month: 5, year: 2026 }),
  datePublished: createDate({ day: 28, month: 6, year: 2026 }),
  version: "3",
  isSeries: false,
  background,
  thumbnail,
};

/* VERSION PAGE */
const Page = () => {
  return (
    <ReplicaContent>
      <ReplicaQuote source="Harry Potter and the Half-Blood Prince">
        Slughorn strode over to a corner cupboard and, after a moment’s
        foraging, emerged with two very battered-looking copies of Advanced
        Potion-Making by Libatius Borage, which he gave to Harry and Ron along
        with two sets of tarnished scales.
      </ReplicaQuote>
      <ReplicaParagraph>
        Although{" "}
        <Link
          to={
            "/" +
            routes.replicaDetail.slug.replace(
              ":id",
              AdvancedPotionMakingPrincesCopy.id,
            )
          }
          target="_blank"
        >
          Prince's copy
        </Link>{" "}
        of Advanced Potion-Making is the most prominent version of this book, it
        is important to note that in the book (Harry Potter and the Half-Blood
        Prince), they are all using the same textbook - as is apparent from the
        quote above. For the film, however, two Editions were designed, each
        basically a unique prop on its own:{" "}
        <Link
          to={
            "/" +
            routes.replicaDetail.slug.replace(
              ":id",
              AdvancedPotionMakingEditionII.id,
            )
          }
          target="_blank"
        >
          Edition II
        </Link>
        , which is the older version that Harry has (
        <Link
          to={
            "/" +
            routes.replicaDetail.slug.replace(
              ":id",
              AdvancedPotionMakingPrincesCopy.id,
            )
          }
          target="_blank"
        >
          Prince's copy
        </Link>
        ), and Edition IV, which is the in-universe current version of the
        textbook. So, while Harry is stuck with the tattered old copy of Edition
        II, all the other students, including Ron, can be seen using a
        completely different prop — this one.
      </ReplicaParagraph>
      <ReplicaParagraph>
        At first glance, the book looks completely different from the older
        Edition — both from the outside and the inside. There will be more
        information about the differences between these two Editions below, but
        it is also important to note how the overall design of this book came
        about. Since this Edition is neither as prolific nor as important as
        Prince’s Copy, gathering reference images proved much more difficult.
        However, I was able to scrape together a minuscule number of references
        — however low quality they may have been — showing pages that were
        actually designed for the film prop. From those, I was able to
        extrapolate the entire visual vocabulary of the book, while also
        introducing some of my own ideas for elements and layouts that were
        never fully designed for the original prop.
      </ReplicaParagraph>
      <ReplicaSpacer />{" "}
      <Gallery
        images={transformImages(gallery)}
        imagesPerRow={8}
        aspectRatio={0.75}
        imageOptions={[
          { index: 0, aspectRatio: 2 },
          { index: 1, aspectRatio: 1 },
          { index: 2, aspectRatio: 2 },
          { index: 3, aspectRatio: 1.5 },
          { index: 5, aspectRatio: 1.5 },
          { index: 7, aspectRatio: 2 },
          { index: 8, aspectRatio: 1.5 },
          { index: 11, aspectRatio: 2 },
          { index: 19, aspectRatio: 1.5 },
        ]}
        imageMaxHeight="180px"
        fillRowAt={GalleryFillRow.start}
      />
      <ReplicaSpacer />
      <YouTubeVideo src="https://www.youtube.com/watch?v=tsYzgrqm-q8" />
      <ReplicaSpacer />
      <ReplicaParagraph>
        The goal of this replica was to recreate Advanced Potion Making –
        Edition IV as the modern version seen being used by the other students
        in Harry Potter and the Half-Blood Prince (the film). It was written and
        formatted as a real-life book, meaning that page numbers in the list of
        contents or in the Potions and Ingredients Index all match the actual
        pages, same as any page numbers mentioned in the text itself when
        referencing other parts of the book. And just like a textbook in real
        life, since this is a newer edition, it also required updating the
        entire text to make it a true upgrade from the previous versions, rather
        than simply reusing the same old text in a different format. The book
        has only very light aging and distressing to reflect a relatively new
        school textbook that has seen minimal usage.
      </ReplicaParagraph>
      <ReplicaSubTitleAnchor title={`Edition II vs Edition IV`} />
      <ReplicaQuote source="Harry Potter and the Half-Blood Prince">
        Although the amount of homework they had been given prevented him from
        reading the whole of his copy of Advanced Potion-Making, he had skimmed
        through it sufficiently to see that there was barely a page on which the
        Prince had not made additional notes, not all of them concerned with
        potion-making. Here and there were directions for what looked like
        spells that the Prince had made up himself.
      </ReplicaQuote>
      <ReplicaParagraph>
        As mentioned above, Edition IV is practically a completely unique prop,
        even if it is technically the successor to Edition II — the version
        later filled with handwritten notes by the Prince. From the outside, it
        is obvious at first glance that these two books have very little in
        common besides the name Advanced Potion-Making. The entire cover design
        is different, including the graphical elements, colors, fonts, and
        overall design language. Not only that, but the dimensions are different
        as well, with Edition IV being slightly larger. And while Edition II
        features silver gilded edges, Edition IV takes a completely different
        approach by using a colorful noise pattern of tiny dots instead — a
        challenge of its own to reproduce reliably and consistently across
        multiple copies.
      </ReplicaParagraph>
      <ReplicaSpacer />
      <Gallery
        images={transformImages(gallery_EditionIIvsIV_1)}
        imagesPerRow={3}
        imageOptions={[
          { index: 0, aspectRatio: 2 },
          { index: 1, aspectRatio: 1 },
        ]}
        imageMaxHeight="400px"
        fillRowAt={GalleryFillRow.end}
      />
      <ReplicaSpacer />
      <ReplicaParagraph>
        From the inside, the changes are just as significant. While the general
        outline of the text remains similar, the content itself was heavily
        revised to ensure that reading Edition II and Edition IV side by side
        would reveal obvious updates, revisions, and expanded information that
        justify the publication of a newer Edition in-universe. On top of that,
        just like the exterior, the entire interior design was completely
        reworked as well. The text had to be reformatted to fit differently
        sized pages, new fonts, and a completely different design vocabulary.
        All potion recipes now also feature ingredient illustrations, which was
        not the case in the older book.
      </ReplicaParagraph>
      <ReplicaSpacer />
      <Gallery
        images={transformImages(gallery_EditionIIvsIV_2)}
        imagesPerRow={4}
        imageOptions={[
          { index: 0, aspectRatio: 2 },
          { index: 1, aspectRatio: 2 },
        ]}
        imageMaxHeight="400px"
        fillRowAt={GalleryFillRow.end}
      />
    </ReplicaContent>
  );
};

export default {
  ...VERSION_DATA,
  page: <Page />,
} as ReplicaVersionType;
